import api from 'api'
import { queryParamSerializer } from 'lib/api/queryParamSerializer'

const toDateOnly = (d: Date | undefined): string | undefined =>
  d?.toISOString().split('T')[0]

export type GetUserPaymentsOptions = {
  userId?: number
  status?: UserPaymentStatus[]
  eventType?: UserPaymentEventType[]
  eventSubType?: UserPaymentEventSubType[]
  type?: string[]
  paymentProvider?: UserPaymentProvider[]
  paymentId?: number
  shiftId?: number
  companyId?: number
  page?: number
  pageSize?: number
  startDate?: Date
  endDate?: Date
}

export enum UserPaymentItemType {
  SHIFT_GROSS_EARNINGS = 'shift_gross_earnings',
  SHIFT_BONUS = 'shift_bonus',
  ELITE_BONUS = 'elite_bonus',
  OAI_DEDUCTION = 'oai_deduction',
  W2_REGULAR = 'w2_regular',
  W2_OVERTIME = 'w2_overtime',
  W2_DOUBLETIME = 'w2_doubletime',
  W2_PAY_NET = 'w2_pay_net',
  W2_PAY_TAX = 'w2_pay_tax',
  REIMBURSEMENT = 'reimbursement',
}

export enum UserPaymentPayingEntity {
  NOT_APPLICABLE = 'not_applicable',
  WORKER = 'worker',
  COMPANY = 'company',
  WORK_WHILE = 'work_while',
}

export enum UserPaymentEventType {
  SHIFT = 'shift',
  INSTANT_PAYOUT = 'instant_payout',
  PAYCHECK = 'paycheck',
  PAYCHECK_W2 = 'paycheck_w2',
  BONUS = 'bonus',
  ADJUSTMENT = 'adjustment',
  REIMBURSEMENT = 'reimbursement',
  AUTOMATIC_PAYOUT = 'automatic_payout',
  EVEREE_PAYOUT = 'everee_payout',
  PAYMENT_REFUND = 'payment_refund',
  ESCHEATMENT = 'escheatment',
  W2_TAX_WITHOLDING = 'w2_tax_witholding',
  W2_DEDUCTION = 'w2_deduction',
  W2_NET_PAY = 'w2_net_pay',
}

export enum UserPaymentEventSubType {
  ELITE_BONUS = 'elite_bonus',
  HEALTH_STIPEND = 'health_stipend',
  MISC = 'misc',
  POST_SHIFT_TRUE_UP = 'post_shift_true_up',
  QUEST_BONUS = 'quest_bonus',
  REFERRAL_BONUS = 'referral_bonus',
  RESEARCH = 'research',
  SICK_CREDIT_BONUS = 'sick_credit_bonus',
  SIGNON_BONUS = 'signon_bonus',
  TIP = 'tip',
  TOLL = 'toll',
  WW_PAID = 'ww_paid',
  FEDERAL_INCOME_TAX = 'federal_income_tax',
  STATE_INCOME_TAX = 'state_income_tax',
  COUNTY_TAX = 'county_tax',
  CITY_TAX = 'city_tax',
  EIT_PENNSYLVANIA = 'eit_pennsylvania',
  LST_PENNSYLVANIA = 'lst_pennsylvania',
  MEDICARE = 'medicare',
  ADDITIONAL_MEDICARE = 'additional_medicare',
  SOCIAL_SECURITY = 'social_security',
  STATE_DISABILITY = 'state_disability',
  FAMILY_LEAVE = 'family_leave',
  STATE_UNEMPLOYMENT = 'state_unemployment',
  GARNISHMENT = 'garnishment',
  CAL_SAVERS = 'cal_savers',
  OVERPAY = 'overpay',
  STATE_RETIREMENT = 'state_retirement',
  OTHER_DEDUCTION = 'other_deduction',
}

export enum UserPaymentStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  FAILED = 'failed',
  REVERSED = 'reversed',
  CANCELED = 'canceled',
}

export enum UserPaymentAdminAction {
  CANCEL_BONUS = 'cancel_bonus',
  PAYOUT_TO_ACH = 'payout_to_ach',
}

export enum UserPaymentProvider {
  EVEREE = 'everee',
  STRIPE = 'stripe',
  MARQETA = 'marqeta',
  BBSI = 'bbsi',
  HIGHNOTE = 'highnote',
}

export type HighnoteTransferAuditEvent = {
  id: number
  type: 'unified_funds_transfer' | 'originated_ach_transfer'
  action: string
  succeeded: boolean
  originatedAt: string
  createdAt: string
  actorType: string | null
  actorId: number | null
  actorName: string | null
  associationId: number | null
  associationLast4: string | null
  webhookEventId: number | null
  highnoteErrorCode: number | null
  highnoteRequestId: string | null
  ipAddress: string | null
  deviceId: string | null
  userAgent: string | null
}

type HighnoteProviderBase = {
  transferId: number
  highnoteId: string | null
  transferStatus: string
  failureReason: string | null
  failureReasonDescription: string | null
  errorCode: number | null
  auditEvents: HighnoteTransferAuditEvent[]
}

export type HighnoteUftProvider = HighnoteProviderBase & {
  transferType: 'unified_funds_transfer'
  paymentMethod: DebitCardPaymentMethod | null
}

export type HighnoteAchProvider = HighnoteProviderBase & {
  transferType: 'originated_ach_transfer'
  traceId?: string
  paymentMethod: BankAccountPaymentMethod | null
}

export type HighnoteProvider = HighnoteUftProvider | HighnoteAchProvider

export type DebitCardPaymentMethod = {
  destinationType: 'debit_card'
  destinationId: number
  accountType: string | null
  displayName: string | null
  last4: string | null
}

export type BankAccountPaymentMethod = {
  destinationType: 'bank_account'
  destinationId: number
  accountType: string | null
  displayName: string | null
  last4: string | null
}

export type PayoutPaymentMethod =
  | DebitCardPaymentMethod
  | BankAccountPaymentMethod

export type RelatedPayment = {
  id: number
  amount: number
  eventType: UserPaymentEventType
  eventSubType: UserPaymentEventSubType | null
  status: UserPaymentStatus
  shiftId: number | null
  positionId: number | null
  positionName: string | null
  companyId: number | null
  companyName: string | null
  shiftStartsAt: string | null
  shiftEndsAt: string | null
}

export type PayoutSummary = {
  id: number
  amount: number
  eventType: UserPaymentEventType
  eventSubType: UserPaymentEventSubType | null
  status: UserPaymentStatus
  statusChangedAt: string | null
  highnote: HighnoteProvider | null
  evereePaymentId: number | null
  paymentMethod: PayoutPaymentMethod | null
  relatedPayments: RelatedPayment[]
}

export type UserPaymentItem = {
  id: number
  amount: number
  itemType: UserPaymentItemType
  payingEntity: UserPaymentPayingEntity
}

type UserPaymentBase = {
  userId: number
  userName: string | null
  userTimezone: string
  id: number
  amount: number
  shiftId: number | null
  positionId: number | null
  positionName: string | null
  companyId: number | null
  companyName: string | null
  comment: string | null
  createdAt: string
  eventType: UserPaymentEventType
  eventSubType: UserPaymentEventSubType | null
  status: UserPaymentStatus
  originatedAt: string
  w2: boolean
  payoutId: number | null
  payout: PayoutSummary | null
  relatedPayments: RelatedPayment[]
  items: UserPaymentItem[]
  availableActions: UserPaymentAdminAction[]
}

type NullProviderFields = {
  stripeId: null
  stripeAccountIdProd: null
  marqetaGpaToken: null
  marqetaAccountId: null
  marqetaEnvironment: null
  evereePaymentId: null
  highnote: null
}

export type HighnoteUserPayment = UserPaymentBase & {
  paymentProvider: UserPaymentProvider.HIGHNOTE
} & NullProviderFields

export type HighnoteUserPaymentWithTransfer = HighnoteUserPayment & {
  eventType: UserPaymentEventType.INSTANT_PAYOUT | UserPaymentEventType.PAYCHECK
}

export type StripeUserPayment = UserPaymentBase & {
  paymentProvider: UserPaymentProvider.STRIPE
  stripeId: string | null
  stripeAccountIdProd: string | null
} & Omit<NullProviderFields, 'stripeId' | 'stripeAccountIdProd'>

export type MarqetaUserPayment = UserPaymentBase & {
  paymentProvider: UserPaymentProvider.MARQETA
  marqetaGpaToken: string | null
  marqetaAccountId: string | null
  marqetaEnvironment: string | null
} & Omit<
    NullProviderFields,
    'marqetaGpaToken' | 'marqetaAccountId' | 'marqetaEnvironment'
  >

export type EvereeUserPayment = UserPaymentBase & {
  paymentProvider: UserPaymentProvider.EVEREE
  evereePaymentId: number | null
} & Omit<NullProviderFields, 'evereePaymentId'>

export type BbsiUserPayment = UserPaymentBase & {
  paymentProvider: UserPaymentProvider.BBSI
} & NullProviderFields

export type UnknownProviderUserPayment = UserPaymentBase &
  NullProviderFields & {
    paymentProvider: null
  }

export type UserPayment =
  | HighnoteUserPayment
  | StripeUserPayment
  | MarqetaUserPayment
  | EvereeUserPayment
  | BbsiUserPayment
  | UnknownProviderUserPayment

export const isPayoutPayment = (eventType: UserPaymentEventType): boolean =>
  eventType === UserPaymentEventType.INSTANT_PAYOUT ||
  eventType === UserPaymentEventType.PAYCHECK ||
  eventType === UserPaymentEventType.EVEREE_PAYOUT

export const hasHighnoteTransfer = (
  payment: UserPayment
): payment is HighnoteUserPaymentWithTransfer =>
  payment.paymentProvider === UserPaymentProvider.HIGHNOTE &&
  isPayoutPayment(payment.eventType)

export const isHighnoteFundAvailablePayment = (
  payment: HighnoteUserPayment
): boolean => payment.payoutId == null && !isPayoutPayment(payment.eventType)

export const getHighnoteAvailableFundLabel = (
  eventType: UserPaymentEventType
): string =>
  eventType === UserPaymentEventType.PAYCHECK ||
  eventType === UserPaymentEventType.PAYCHECK_W2
    ? 'Fund available for paycheck'
    : 'Fund available for cashout'

export type GetUserPaymentsResponse = {
  items: UserPayment[]
  totalRows: number
  totalPages: number
  currentPage: number
  pageSize: number
}

const normalizePayoutSummary = (
  payout: PayoutSummary | null | undefined
): PayoutSummary | null => {
  if (!payout) {
    return null
  }

  const rawPayout = payout as PayoutSummary & {
    related_payments?: RelatedPayment[]
  }

  return {
    ...payout,
    relatedPayments: payout.relatedPayments ?? rawPayout.related_payments ?? [],
    evereePaymentId: payout.evereePaymentId ?? null,
    highnote: payout.highnote
      ? {
          ...payout.highnote,
          auditEvents: payout.highnote.auditEvents ?? [],
        }
      : null,
  }
}

const normalizeUserPayment = (payment: UserPayment): UserPayment => {
  const rawPayment = payment as UserPayment & {
    related_payments?: RelatedPayment[]
  }

  return {
    ...payment,
    relatedPayments:
      payment.relatedPayments ?? rawPayment.related_payments ?? [],
    availableActions: payment.availableActions ?? [],
    payout: normalizePayoutSummary(payment.payout),
  }
}

export const getUserPayments = async ({
  userId,
  status,
  eventType,
  eventSubType,
  type,
  paymentProvider,
  paymentId,
  shiftId,
  companyId,
  page = 0,
  pageSize = 50,
  startDate,
  endDate,
}: GetUserPaymentsOptions): Promise<GetUserPaymentsResponse> => {
  const response = await api.get('/admin/user_payments', {
    params: {
      user_id: userId,
      status,
      event_type: eventType,
      event_sub_type: eventSubType,
      type,
      payment_provider: paymentProvider,
      payment_id: paymentId,
      shift_id: shiftId,
      company_id: companyId,
      page,
      page_size: pageSize,
      start_date: toDateOnly(startDate),
      end_date: toDateOnly(endDate),
    },
    paramsSerializer: queryParamSerializer,
  })
  return {
    ...response.data,
    items: response.data.items.map(normalizeUserPayment),
  }
}
